import { SVG_ENUM } from "enums";
import React, { useState } from "react";
import { Button, SVG } from "shared";
import { UserType } from "types/UsersTypes";
import "./Payments.scss";
import { DeleteSubscriptionModal, PacketModal } from "features/users";
import { AddPaymentMethodModal, DeleteCardModal } from "./components";
import { useGetMe } from "hooks/auth";
import {
  useGetPaymentMethods,
  useGetPayments,
  usePayInvoice,
  useConfirmCardPayment,
  UpdatePaymentMethodHook,
  DeletePaymentMethodHook,
  useCancelProUpgrade,
} from "hooks/payments";
import { PACKET_PRICE_ENUM, PACKET_ENUM } from "enums/PacketsEnum";
import {
  UpdatePaymentMethodType,
  DeletePaymentMethodType,
} from "types/PaymentTypes";
import { useGetSubscription } from "hooks/subscriptions";
import { hasNextPayment } from "utils/subscriptionUtils";
import moment from "moment";
import { useGetCustomer } from "hooks/customers";
import { getDiscountedPrice } from "utils/discountUtils";
import InvoiceMailing from "./components/InvoiceMailing";
import { queryClient } from "App";
import { pollMeForPackage } from "utils";

interface PaymentsProps {
  user: UserType | undefined;
  handleGoBack: () => void;
}

const Payments: React.FC<PaymentsProps> = ({ user, handleGoBack }) => {
  const [isPacketModalOpen, setIsPacketModalOpen] = useState(false);
  const [isDeleteSubscriptionModalOpen, setIsDeleteSubscriptionModalOpen] =
    useState(false);
  const [isAddPaymentMethodModalOpen, setIsAddPaymentMethodModalOpen] =
    useState(false);
  const [selectedCardToDelete, setSelectedCardToDelete] =
    useState<DeletePaymentMethodType>();
  const [isDeleteCardModalOpen, setIsDeleteCardModalOpen] = useState(false);
  const { data: me, refetch: refetchMe } = useGetMe();
  const { data: paymentMethods, refetch: refetchCardsList } =
    useGetPaymentMethods();
  const { data: payments, refetch: refetchPayments } = useGetPayments();
  const { data: customer } = useGetCustomer(!!me);
  const { data: subscription, refetch: refetchSubscription } =
    useGetSubscription();
  const isDeleteSubscription = !!subscription?.canceled_at && customer?.plan;
  const isDowngrade = !!customer?.planDowngradedTo && !isDeleteSubscription;
  const isDefaultPaymentMethod = paymentMethods?.find(
    (paymentMethod) => paymentMethod.default,
  );
  const isNonPayingProUser =
    !me?.package &&
    customer?.plan === PACKET_ENUM.PRO &&
    subscription?.status !== "active";

  const isFailedUpgrade =
    !!me?.package &&
    !!customer?.plan &&
    me.package !== customer.plan &&
    !!customer?.planChangeInitiatedAt &&
    !customer?.planDowngradedTo &&
    !isDeleteSubscription;

  // Znajdź nieopłaconą fakturę związaną z subskrypcją
  const unpaidSubscriptionInvoice = payments?.find((payment: any) => {
    const invoiceStatus = payment?.status as string | undefined;
    const isCanceled =
      invoiceStatus === "void" || invoiceStatus === "uncollectible";
    const isPaid = payment.paid || invoiceStatus === "paid";
    const isSubscriptionInvoice = payment.subscription === subscription?.id;
    return !isPaid && !isCanceled && isSubscriptionInvoice;
  });

  const { mutate: updatePaymentMethod } = UpdatePaymentMethodHook({
    onSuccess: () => {
      refetchCardsList();
    },
  });
  const { mutate: deletePaymentMethod } = DeletePaymentMethodHook();

  const { mutateAsync: payInvoice } = usePayInvoice();
  const { confirmCardPayment } = useConfirmCardPayment();
  const { mutate: cancelProUpgrade } = useCancelProUpgrade();

  const choosePaymentMethod = async (id: UpdatePaymentMethodType) => {
    await updatePaymentMethod(id);
  };
  const deleteCard = async (id: DeletePaymentMethodType) => {
    await deletePaymentMethod(id);
  };

  const handleDeleteCard = (id: DeletePaymentMethodType, last4: string) => {
    setSelectedCardToDelete(id);
    setIsDeleteCardModalOpen(true);
  };

  const confirmDeleteCard = async () => {
    if (selectedCardToDelete) {
      await deleteCard(selectedCardToDelete);
      setIsDeleteCardModalOpen(false);
    }
  };

  const handleRetryInvoicePayment = async (invoiceUid: string) => {
    let data;
    try {
      data = await payInvoice({ invoice_uid: invoiceUid });
    } catch (error) {
      console.error("payInvoice error:", error);
      return;
    }

    const clientSecret = data?.payment_intent_client_secret;
    if (!clientSecret) {
      if (data?.hosted_invoice_url) {
        window.open(data.hosted_invoice_url, "_blank", "noreferrer");
      }
      return;
    }

    const result = await confirmCardPayment(clientSecret);
    if (!result.ok) {
      return;
    }

    await refetchPayments();
    await refetchSubscription();
    await pollMeForPackage(refetchMe, PACKET_ENUM.PRO);
    queryClient.invalidateQueries({ queryKey: ["payments"] });
  };

  return (
    <div className="profile__right-wrapper payments">
      <PacketModal
        isOpen={isPacketModalOpen}
        onClose={() => setIsPacketModalOpen(false)}
        parent="payment"
      />

      <DeleteSubscriptionModal
        isOpen={isDeleteSubscriptionModalOpen}
        onClose={() => setIsDeleteSubscriptionModalOpen(false)}
        customer={customer}
      />

      <AddPaymentMethodModal
        isOpen={isAddPaymentMethodModalOpen}
        onClose={() => setIsAddPaymentMethodModalOpen(false)}
        skipSubscriptionActivation
      />

      <DeleteCardModal
        isOpen={isDeleteCardModalOpen}
        onClose={() => setIsDeleteCardModalOpen(false)}
        onConfirm={confirmDeleteCard}
        cardLastFour={
          paymentMethods?.find((pm) => pm.id === selectedCardToDelete)?.card
            .last4
        }
      />

      <div className="profile__right-back" onClick={() => handleGoBack()}>
        <span className="profile__right-back-icon">
          <SVG type={SVG_ENUM.ARROW_BACK} />
        </span>
        Wróć
      </div>
      <h2 className="profile__menu-header">
        <span className="profile__menu-icon profile__menu-icon--right">
          <SVG type={SVG_ENUM.CREDIT_CARD} />
        </span>
        Płatności
      </h2>
      <div className="payments__package-wrapper">
        <p className="payments__label">
          {isDowngrade || isFailedUpgrade ? (
            "Twój obecny pakiet"
          ) : (
            <>
              {isNonPayingProUser ? (
                <span className="payments__label--non-paying">
                  Twój pakiet nie został aktywowany (płatność nie powiodła się)
                </span>
              ) : (
                <span className="bold">Twój pakiet</span>
              )}
            </>
          )}
        </p>
        {isFailedUpgrade ? (
          <div className="payments__package">
            <SVG
              type={SVG_ENUM.LOGIN_BG_MOBILE}
              className="payments__package-bg"
            />
            <div className="payments__package-left">
              <p className="payments__package-name">Pakiet {me?.package}</p>
              <p>
                <span className="payments__package-price">
                  {
                    PACKET_PRICE_ENUM[
                      me?.package?.toLocaleUpperCase() as keyof typeof PACKET_PRICE_ENUM
                    ]
                  }
                </span>
                zł/msc
              </p>
            </div>
          </div>
        ) : customer?.plan ? (
          <div
            className={`payments__package ${
              isNonPayingProUser ? "payments__package--non-paying" : ""
            }`}
          >
            <SVG
              type={SVG_ENUM.LOGIN_BG_MOBILE}
              className={`payments__package-bg ${
                isNonPayingProUser ? "payments__package-bg--non-paying" : ""
              }`}
            />
            <div className="payments__package-left">
              <p className="payments__package-name">Pakiet {customer?.plan}</p>
              <p>
                <span className="payments__package-price">
                  {getDiscountedPrice(customer?.plan, customer?.discounts)}
                </span>
                zł/msc
              </p>
            </div>
            {isNonPayingProUser ? (
              <div className="payments__package-buttons">
                <Button
                  label="Zmień"
                  className="button-rounded payments__package-button"
                  onClick={() =>
                    isDefaultPaymentMethod
                      ? setIsPacketModalOpen(true)
                      : setIsAddPaymentMethodModalOpen(true)
                  }
                />
                <Button
                  label="Ponów płatność"
                  className="button--rounded button--black payments__package-button-return"
                  onClick={() => {
                    if (unpaidSubscriptionInvoice) {
                      handleRetryInvoicePayment(unpaidSubscriptionInvoice.id);
                    } else if (isDefaultPaymentMethod) {
                      setIsPacketModalOpen(true);
                    } else {
                      setIsAddPaymentMethodModalOpen(true);
                    }
                  }}
                />
              </div>
            ) : isDowngrade || isDeleteSubscription ? null : (
              <Button
                label="Zmień"
                className="button-rounded payments__package-button"
                onClick={() =>
                  isDefaultPaymentMethod
                    ? setIsPacketModalOpen(true)
                    : setIsAddPaymentMethodModalOpen(true)
                }
              />
            )}
          </div>
        ) : (
          <Button
            label="Wybierz pakiet"
            size="small"
            className="button--stroke button--rounded button--payments payments__choose-packet"
            onClick={() => {
              setIsPacketModalOpen(true);
              sessionStorage.removeItem("submitting");
            }}
          />
        )}
      </div>
      {isDowngrade ? (
        <>
          {" "}
          <p className="payments__label payments__label--downgrade">
            Zmiana pakietu od{" "}
            <p className=" payments__label--downgrade-gray">
              {subscription?.current_period_end &&
                moment
                  .unix(subscription?.current_period_end)
                  .locale("pl")
                  .format("D MMMM YYYY")}
            </p>
          </p>
          <div className="payments__package">
            <SVG
              type={SVG_ENUM.LOGIN_BG_MOBILE}
              className="payments__package-bg"
            />
            <div className="payments__package-left">
              <p className="payments__package-name">
                Pakiet {customer?.planDowngradedTo}
              </p>
              <p>
                <span className="payments__package-price">
                  {getDiscountedPrice(
                    customer?.planDowngradedTo,
                    customer?.discounts,
                  )}
                </span>
                zł/msc
              </p>
            </div>
          </div>
        </>
      ) : null}

      {isFailedUpgrade ? (
        <>
          <p className="payments__label payments__label--downgrade">
            Zmiana pakietu od{" "}
            <p className="payments__label--downgrade-gray">
              {customer?.planChangeInitiatedAt &&
                moment(customer.planChangeInitiatedAt)
                  .locale("pl")
                  .format("D MMMM YYYY")}
            </p>
          </p>
          <p className="payments__label payments__label--non-paying payments__label--failed-upgrade">
            Twój pakiet nie został aktywowany (płatność nie powiodła się)
          </p>
          <div className="payments__package payments__package--non-paying">
            <SVG
              type={SVG_ENUM.LOGIN_BG_MOBILE}
              className="payments__package-bg payments__package-bg--non-paying"
            />
            <div className="payments__package-left">
              <p className="payments__package-name">Pakiet {customer?.plan}</p>
              <p>
                <span className="payments__package-price">
                  {
                    PACKET_PRICE_ENUM[
                      customer?.plan?.toLocaleUpperCase() as keyof typeof PACKET_PRICE_ENUM
                    ]
                  }
                </span>
                zł/msc
              </p>
            </div>
            <div className="payments__package-buttons">
              <Button
                label="Anuluj"
                className="button-rounded payments__package-button"
                onClick={() => cancelProUpgrade()}
              />
              <Button
                label="Ponów płatność"
                className="button--rounded button--black payments__package-button-return"
                onClick={() => {
                  if (unpaidSubscriptionInvoice) {
                    handleRetryInvoicePayment(unpaidSubscriptionInvoice.id);
                  } else if (isDefaultPaymentMethod) {
                    setIsPacketModalOpen(true);
                  } else {
                    setIsAddPaymentMethodModalOpen(true);
                  }
                }}
              />
            </div>
          </div>
        </>
      ) : null}

      {isDeleteSubscription ? (
        <p className="payments__label payments__label--downgrade">
          Rezygnacja od{" "}
          <p className=" payments__label--downgrade-gray">
            {subscription?.canceled_at &&
              subscription.current_period_end &&
              moment
                .unix(subscription?.current_period_end)
                .locale("pl")
                .format("D MMMM YYYY")}
          </p>
        </p>
      ) : customer?.plan && !isFailedUpgrade ? (
        <p
          className="payments__deactivate"
          onClick={() => setIsDeleteSubscriptionModalOpen(true)}
        >
          Rezygnuj z subskrybcji
        </p>
      ) : null}
      <div className="payments__payment-methods">
        <div className="payments__payment-methods--header">
          <p className="payments__label">Metody płatności</p>
          <Button
            onClick={() => {
              setIsAddPaymentMethodModalOpen(true);
              sessionStorage.removeItem("packet");
            }}
            className="button--stroke button--rounded button--payments"
            size="small"
            label="Dodaj kartę płatniczą +"
          />
        </div>
        <div className="payments__payment-methods--items-wrapper">
          {!!paymentMethods.length && (
            <p className="payments__payment-methods--label">Zapisane karty</p>
          )}
          {paymentMethods?.map((paymentMethod, key) => (
            <>
              <div className="payments__payment-methods--item" key={key}>
                <div
                  className="payments__payment-methods--toggler"
                  onClick={() =>
                    !paymentMethod.default &&
                    choosePaymentMethod(paymentMethod.id)
                  }
                />
                <SVG
                  type={SVG_ENUM.CARD}
                  className="payments__payment-methods--card"
                />
                <p className="payments__payment-methods--number">
                  ****{paymentMethod.card.last4}
                </p>
                {paymentMethod.default ? (
                  <>
                    <div className="payments__payment-methods--options">
                      Wybrano
                    </div>
                    <SVG
                      type={SVG_ENUM.CIRCLE_CHECKED_GREEN}
                      className="payments__payment-methods--checked"
                    />
                    {!customer?.plan && (
                      <div
                        className="payments__payment-methods--options"
                        onClick={() =>
                          handleDeleteCard(
                            paymentMethod.id,
                            paymentMethod.card.last4,
                          )
                        }
                      >
                        <SVG
                          type={SVG_ENUM.TRASH}
                          className="payments__payment-methods--trash"
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    className="payments__payment-methods--options"
                    onClick={() =>
                      handleDeleteCard(
                        paymentMethod.id,
                        paymentMethod.card.last4,
                      )
                    }
                  >
                    <SVG
                      type={SVG_ENUM.TRASH}
                      className="payments__payment-methods--trash"
                    />
                  </div>
                )}
              </div>
            </>
          ))}
        </div>
      </div>

      <div className="payments__invoices">
        <div className="payments__invoices--header">
          <p className="payments__label">Faktury</p>
          {subscription && hasNextPayment(subscription) && (
            <p className="payments__invoices--label">
              Następna płatność:{" "}
              <p className="payments__invoices--label--bold">
                {" "}
                {moment
                  .unix(subscription?.current_period_end!)
                  .format("DD/MM/YYYY")}
              </p>
            </p>
          )}
          {!payments?.filter(
            (p: any) => p?.status !== "void" && p?.status !== "uncollectible",
          ).length && (
            <p className="payments__subtitle">
              W tym miejscu pojawią się Twoje faktury.
            </p>
          )}
        </div>
        <InvoiceMailing />
        <div className="payments__invoices--items-wrapper">
          {payments
            ?.filter(
              (p: any) => p?.status !== "void" && p?.status !== "uncollectible",
            )
            .map((payment) => {
              const invoiceStatus = (payment as any)?.status as
                | "draft"
                | "open"
                | "paid"
                | "uncollectible"
                | "void"
                | string
                | undefined;
              const isCanceled =
                invoiceStatus === "void" || invoiceStatus === "uncollectible";
              const isPaid = payment.paid || invoiceStatus === "paid";
              // const isRetryable = !isPaid && !isCanceled;

              return (
                <div className="payments__invoices--item" key={payment.id}>
                  <p className="payments__invoices--date">
                    &ensp; {moment.unix(payment?.created).format("DD/MM/YYYY")}
                  </p>
                  <p className="payments__invoices--amount">
                    {payment.total / 100} zł
                  </p>
                  <p
                    className={`payments__invoices--status ${
                      isPaid ? "paid" : isCanceled ? "canceled" : "unpaid"
                    }`}
                    // onClick={
                    //   isRetryable
                    //     ? () => handleRetryInvoicePayment(payment.id)
                    //     : undefined
                    // }
                  >
                    {isPaid ? (
                      "Opłacona"
                    ) : isCanceled ? (
                      "Anulowana"
                    ) : (
                      <>Nieopłacona</>
                    )}
                  </p>
                  <a
                    className="payments__download"
                    href={payment.invoice_pdf}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <SVG
                      type={SVG_ENUM.FILE}
                      className="payments__invoices--cloud-icon"
                    />
                    <p className="payments__invoices--download">Pobierz</p>
                  </a>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default Payments;
